import {
  ChangeAsyncHandler,
  ChangeHandler,
  ChangeManager,
  ChangeManagerSubscriber,
  ChangeSyncHandler,
  isChangeCallbackHandler,
} from './changeManager';

/*
 * Types.
 */

export interface BaseChangeManagerConfig {
  subscribe: (subscriber: () => void) => () => void;
  /** Covers implementation-specific post-processing changes that should be ignored. */
  postChangeHandler?: ChangeHandler;
}

type ChangeToken = object;

/*
 * Class.
 */

export class BaseChangeManager implements ChangeManager {
  constructor(config: BaseChangeManagerConfig) {
    this.config = config;
    this.postChangeHandler = config.postChangeHandler
      ? promisifyChangeHandler(config.postChangeHandler)
      : undefined;
    this.isDirtyPrivate = false;
  }

  private readonly config: BaseChangeManagerConfig;
  /** Promisified version of config.postChangeHandler. */
  private readonly postChangeHandler?: ChangeAsyncHandler | ChangeSyncHandler;
  private isDirtyPrivate: boolean;
  /** Dirty marking is suspended while this is defined. */
  private changeToken: ChangeToken | undefined;
  private unsubscribeHandler: (() => void) | undefined;

  get isDirty() {
    return this.isDirtyPrivate;
  }

  async ignoreChanges(changeHandler?: ChangeHandler): Promise<void> {
    const changeToken = generateChangeToken();
    this.changeToken = changeToken;

    await this.performChanges(changeHandler);

    // Check whether our token is still current.
    if (this.changeToken !== changeToken) {
      return;
    }

    this.changeToken = undefined;
  }

  markAsPristine(resetHandler?: ChangeHandler): void {
    this.isDirtyPrivate = false;
    this.ignoreChanges(resetHandler);
  }

  markAsDirty(): void {
    this.isDirtyPrivate = true;
    this.changeToken = undefined;
  }

  private maybeMarkAsDirty() {
    if (this.isDirtyPrivate || this.changeToken) {
      return;
    }

    this.isDirtyPrivate = true;
  }

  private async performChanges(changeHandler?: ChangeHandler): Promise<void> {
    // Perform the provided callback.
    if (changeHandler) {
      const promisifiedChangeHandler = promisifyChangeHandler(changeHandler);
      await promisifiedChangeHandler();
    }

    // Perform the implementation-specific callback.
    if (this.postChangeHandler) {
      await this.postChangeHandler();
    }
  }

  subscribe(subscriber: ChangeManagerSubscriber, markAsPristine = true) {
    this.unsubscribe();

    if (markAsPristine) {
      this.markAsPristine();
    }

    this.unsubscribeHandler = this.config.subscribe(() => {
      this.maybeMarkAsDirty();
      subscriber(this.isDirtyPrivate);
    });
  }

  unsubscribe() {
    if (!this.unsubscribeHandler) {
      return;
    }

    this.unsubscribeHandler();
    this.unsubscribeHandler = undefined;
  }
}

/*
 * Helpers.
 */

function generateChangeToken(): ChangeToken {
  // Simply use a new object reference as a unique token.
  return {};
}

function promisifyChangeHandler(handler: ChangeHandler): ChangeAsyncHandler | ChangeSyncHandler {
  if (isChangeCallbackHandler(handler)) {
    return () =>
      new Promise((resolve) => {
        handler(() => {
          resolve();
        });
      });
  }

  return handler;
}
